import { Component, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/accessibility';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import { SimulationResponse } from '../../../core/models/simulation.model';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import { LoadingService } from '../../../services/loading.service';
import { CubeTrendDataPoint, TrendCoefficients } from './cube-trend-formation-factor-results.model';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { SimulationDirectionsTableComponent } from '../../simulation-directions-table/simulation-directions-table.component';
import { map, merge } from 'rxjs';
import {
  createBaseLogChartOptions,
  createScatterSeries,
  createTrendLineSeries,
  updateChartSeriesVisibilitySmooth,
} from '../../../utils/chart/chart-config.util';
import { formatValue } from '../../../utils/format.util';

@Component({
  selector: 'app-cube-trend-formation-factor-results',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    HighchartsChartModule,
    SimulationOverviewComponent,
    SimulationDirectionsTableComponent
],
  templateUrl: './cube-trend-formation-factor-results.component.html',
  styleUrl: './cube-trend-formation-factor-results.component.scss',
})
export class CubeTrendFormationFactorResultsComponent extends Destroyable(Object) implements OnInit {
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly componentId = 'cube-trend-ff-results';

  simulation: SimulationResponse<any> | null = null;
  simulationId: string | null = null;
  isLoading = false;

  directions: string[] = [];
  cubeSizes: Array<{ cubeSizeVoxels: number; cubeSizeFraction: number }> = [];
  directionsForm!: FormGroup;
  cubeSizesForm!: FormGroup;
  selectedDirections: Record<string, boolean> = {};
  selectedCubeSizes: Record<string, boolean> = {};

  get simulationResponse(): SimulationResponse<any> | null {
    return this.simulation;
  }

  chartOptions?: Highcharts.Options;
  chart?: Highcharts.Chart;

  dataPoints: CubeTrendDataPoint[] = [];
  trendData: Map<string, { direction: string, cubeSize: number, points: Array<{ connectedPorosity: number, simulationValue: number }> }> = new Map();

  coefficients: TrendCoefficients[] = [];

  allResultsInvalid: boolean = false;
  isDataSelected = false;

  private readonly colorScheme: Record<string, string[]> = {
    x: ['#003D99', '#0066CC', '#0099FF'],
    y: ['#1F4620', '#2D7A3E', '#4CAF50'],
    z: ['#CC5200', '#FF8C00', '#FFB347'],
  };

  private readonly sizeSymbolMap: string[] = ['circle', 'triangle', 'square'];

  constructor(
    private route: ActivatedRoute,
    private simulationsService: SimulationsService,
    private loadingService: LoadingService,
    private fb: FormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    this.simulationId = this.route.snapshot.paramMap.get('id');
    this.subscribeToLoadingState();

    if (this.simulationId) {
      this.loadSimulation(this.simulationId);
    }
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });
  }

  private loadSimulation(id: string): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(id, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((simulation) => {
        this.simulation = simulation;
        this.directions = this.simulation.direction.split('');

        this.parseSimulationData();

        this.initializeSelections();

        this.allResultsInvalid = this.dataPoints.length === 0;

        this.initializeChart();
      });
  }

  private parseSimulationData(): void {
    if (!this.simulation?.results) return;

    this.dataPoints = [];
    this.coefficients = [];
    this.trendData.clear();
    const uniqueSizesMap = new Map<number, { cubeSizeVoxels: number; cubeSizeFraction: number }>();

    this.simulation.results.forEach((result: any) => {
      if (result.result && result.result.cubeSizeResults) {
        const direction = result.direction.toLowerCase();
        const status = result.status?.toLowerCase() || 'failed';

        // Only process successful results
        if (status !== 'success') return;

        result.result.cubeSizeResults.forEach((cubeSizeResult: any) => {
          // Store both voxels and fraction
          if (!uniqueSizesMap.has(cubeSizeResult.cubeSizeVoxels)) {
            uniqueSizesMap.set(cubeSizeResult.cubeSizeVoxels, {
              cubeSizeVoxels: cubeSizeResult.cubeSizeVoxels,
              cubeSizeFraction: cubeSizeResult.cubeSizeFraction
            });
          }

          // Extract coefficients from backend (per direction and size)
          if (cubeSizeResult.angularCoefficient !== undefined && 
              cubeSizeResult.exponentialCoefficient !== undefined) {
            const sortedSizes = Array.from(uniqueSizesMap.values()).sort((a, b) => a.cubeSizeVoxels - b.cubeSizeVoxels);
            const sizeIndex = sortedSizes.findIndex(s => s.cubeSizeVoxels === cubeSizeResult.cubeSizeVoxels);
            const directionColor = this.colorScheme[direction]?.[sizeIndex] || '#000000';
            
            this.coefficients.push({
              direction: direction.toUpperCase(),
              cubeSize: cubeSizeResult.cubeSizeVoxels,
              angularCoefficient: cubeSizeResult.angularCoefficient,
              exponentialCoefficient: cubeSizeResult.exponentialCoefficient,
              rSquared: 0, // R² not provided by backend in this structure
              color: directionColor,
            });
          }

          // Store trend points from backend
          if (cubeSizeResult.trendPoints && Array.isArray(cubeSizeResult.trendPoints)) {
            const key = `${direction}-${cubeSizeResult.cubeSizeVoxels}`;
            this.trendData.set(key, {
              direction: direction,
              cubeSize: cubeSizeResult.cubeSizeVoxels,
              points: cubeSizeResult.trendPoints.map((tp: any) => ({
                connectedPorosity: tp.connectedPorosity,
                simulationValue: tp.simulationValue
              }))
            });
          }

          // Convert samples to data points
          if (cubeSizeResult.samples && Array.isArray(cubeSizeResult.samples)) {
            cubeSizeResult.samples.forEach((sample: any) => {
              // Filter out invalid data
              const isValidFF = sample.simulationValue > 0 && sample.simulationValue < 1000000;
              const isValidPorosity = sample.connectedPorosity > 0;

              if (isValidFF && isValidPorosity) {
                this.dataPoints.push({
                  direction: direction,
                  cubeSize: cubeSizeResult.cubeSizeVoxels,
                  connectedPorosity: sample.connectedPorosity,
                  formationFactor: sample.simulationValue,
                  totalPorosity: sample.totalPorosity,
                  startX: sample.startX,
                  startY: sample.startY,
                  startZ: sample.startZ,
                });
              }
            });
          }
        });
      }
    });

    this.cubeSizes = Array.from(uniqueSizesMap.values()).sort((a, b) => a.cubeSizeVoxels - b.cubeSizeVoxels);
  }

  private initializeSelections(): void {
    this.initDirectionForm();
    this.initCubeSizeForm();

    this.directions.forEach(direction => {
      const hasValidData = this.dataPoints.some(
        point => point.direction === direction.toLowerCase()
      );
      if (hasValidData) {
        this.selectedDirections['direction' + direction] = true;
        this.directionsForm.get(direction)?.setValue(true, { emitEvent: false });
        this.directionsForm.get(direction)?.enable({ emitEvent: false });
      }
    });

    this.cubeSizes.forEach(size => {
      const hasData = this.dataPoints.some(point => point.cubeSize === size.cubeSizeVoxels);
      if (hasData) {
        this.selectedCubeSizes['size' + size.cubeSizeVoxels] = true;
        this.cubeSizesForm.get(size.cubeSizeVoxels.toString())?.setValue(true, { emitEvent: false });
        this.cubeSizesForm.get(size.cubeSizeVoxels.toString())?.enable({ emitEvent: false });
      }
    });

    this.updateDataSelection();
  }

  private initDirectionForm(): void {
    const formControls: any = {};
    this.directions.forEach(direction => {
      formControls[direction] = [{ value: false, disabled: true }];
    });

    this.directionsForm = this.fb.group(formControls);

    const directionObservables = this.directions.map(direction =>
      this.directionsForm.get(direction)!.valueChanges.pipe(
        map(value => ({ direction, value }))
      )
    );

    merge(...directionObservables)
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ direction, value }) => {
        this.selectedDirections['direction' + direction] = value;
        this.updateDataSelection();
        this.updateChart();
      });
  }

  private initCubeSizeForm(): void {
    const formControls: any = {};
    this.cubeSizes.forEach(size => {
      formControls[size.cubeSizeVoxels.toString()] = [{ value: false, disabled: true }];
    });

    this.cubeSizesForm = this.fb.group(formControls);

    const sizeObservables = this.cubeSizes.map(size =>
      this.cubeSizesForm.get(size.cubeSizeVoxels.toString())!.valueChanges.pipe(
        map(value => ({ size: size.cubeSizeVoxels, value }))
      )
    );

    merge(...sizeObservables)
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ size, value }) => {
        this.selectedCubeSizes['size' + size] = value;
        this.updateDataSelection();
        this.updateChart();
      });
  }

  getDirectionControl(direction: string): FormControl {
    return this.directionsForm.get(direction) as FormControl;
  }

  getCubeSizeControl(size: number): FormControl {
    return this.cubeSizesForm.get(size.toString()) as FormControl;
  }

  private initializeChart(): void {
    // Initialize chart with empty series - series will be added dynamically via updateChartSeriesVisibility
    this.chartOptions = createBaseLogChartOptions({
      title: 'Formation Factor (FF) Trend',
      xAxisTitle: 'Porosity (%)',
      yAxisTitle: 'Formation Factor',
      series: [], // Start with empty series
      xAxisType: 'logarithmic',
      yAxisType: 'logarithmic',
      tooltipFormatter: function() {
        const xVal = typeof this.x === 'number' ? this.x : 0;
        const yVal = typeof this.y === 'number' ? this.y : 0;
        return `<b>${this.series.name}</b><br/>Porosity: ${xVal.toFixed(2)}%<br/>FF: ${yVal.toFixed(2)}`;
      },
    });

    // Override legend layout for cube trends
    this.chartOptions!.legend = {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    };

    // Enable exporting with context menu
    this.chartOptions!.exporting = {
      enabled: true,
      fallbackToExportServer: false,
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
          ],
        },
      },
    };
  }

  saveChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    // Initialize chart with all series upfront for smooth visibility toggling
    this.initializeChartSeries();
  }

  private initializeChartSeries(): void {
    if (!this.chart) return;

    const allSeries = this.generateChartSeries();

    // Add all series to the chart upfront
    allSeries.forEach(series => {
      this.chart!.addSeries(series, false);
    });

    this.chart.redraw();

    // Apply initial visibility based on form state
    this.updateChartVisibility();
  }

  toggleDirection(direction: string): void {
    const control = this.directionsForm.get(direction);
    if (control && !control.disabled) {
      control.setValue(!control.value);
    }
  }

  toggleCubeSize(size: number): void {
    const control = this.cubeSizesForm.get(size.toString());
    if (control && !control.disabled) {
      control.setValue(!control.value);
    }
  }

  private updateDataSelection(): void {
    this.isDataSelected =
      Object.values(this.selectedDirections).some(Boolean) &&
      Object.values(this.selectedCubeSizes).some(Boolean);
  }

  private generateChartSeries(): Highcharts.SeriesOptionsType[] {
    const series: Highcharts.SeriesOptionsType[] = [];
    const allData = this.dataPoints;

    const groups = this.groupDataPoints(allData);

    // Add scatter series for samples (one per direction-size combination)
    // Generate ALL series, they will be shown/hidden based on selection
    Object.entries(groups).forEach(([key, points]) => {
      const [direction, cubeSize] = key.split('-');
      const size = parseInt(cubeSize);

      const data = points
        .filter(p => p.totalPorosity > 0 && p.formationFactor > 0)
        .map(point => [
          point.totalPorosity < 1 ? point.totalPorosity * 100 : point.totalPorosity,
          point.formationFactor,
        ] as [number, number]);

      if (data.length > 0) {
        const sizeIndex = this.cubeSizes.findIndex(s => s.cubeSizeVoxels === size);
        const color = this.colorScheme[direction]?.[sizeIndex] || '#000000';
        const symbol = this.sizeSymbolMap[sizeIndex] || 'circle';

        series.push(
          createScatterSeries({
            name: `${direction.toUpperCase()} Size ${size}`,
            data: data,
            color: color,
            symbol: symbol,
          })
        );
      }
    });

    // Add trend lines from backend data (one per direction-size combination)
    this.trendData.forEach((trendInfo, key) => {
      const [direction, cubeSize] = key.split('-');
      const size = parseInt(cubeSize);
      
      if (trendInfo.points.length > 0) {
        const sizeIndex = this.cubeSizes.findIndex(s => s.cubeSizeVoxels === size);
        const color = this.colorScheme[direction]?.[sizeIndex] || '#000000';
        
        // Use trend points directly without transformation
        const trendData = trendInfo.points
          .filter(p => p.connectedPorosity > 0 && p.simulationValue > 0)
          .map(p => [
            p.connectedPorosity < 1 ? p.connectedPorosity * 100 : p.connectedPorosity,
            p.simulationValue,
          ] as [number, number]);

        if (trendData.length > 0) {
          series.push(
            createTrendLineSeries({
              name: `Trend ${direction.toUpperCase()} Size ${size}`,
              data: trendData,
              color: color,
            })
          );
        }
      }
    });

    return series;
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.updateChartVisibility();
  }

  private updateChartVisibility(): void {
    if (!this.chart) return;

    // Determine which series should be visible based on current selection
    const selectedSeriesNames = new Set<string>();
    
    const directions = Object.keys(this.selectedDirections)
      .filter(k => this.selectedDirections[k])
      .map(k => k.replace('direction', ''));
    
    const sizes = Object.keys(this.selectedCubeSizes)
      .filter(k => this.selectedCubeSizes[k])
      .map(k => parseInt(k.replace('size', '')));

    // Add both scatter and trend series names for selected combinations
    directions.forEach(direction => {
      sizes.forEach(size => {
        selectedSeriesNames.add(`${direction} Size ${size}`);
        selectedSeriesNames.add(`Trend ${direction} Size ${size}`);
      });
    });

    updateChartSeriesVisibilitySmooth(this.chart, selectedSeriesNames);
  }

  private groupDataPoints(points: CubeTrendDataPoint[]): Record<string, CubeTrendDataPoint[]> {
    const groups: Record<string, CubeTrendDataPoint[]> = {};

    points.forEach(point => {
      const key = `${point.direction}-${point.cubeSize}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(point);
    });

    return groups;
  }

  getSelectedDataPoints(): CubeTrendDataPoint[] {
    return this.dataPoints.filter(point => {
      const directionSelected = this.selectedDirections['direction' + point.direction.toUpperCase()];
      const sizeSelected = this.selectedCubeSizes['size' + point.cubeSize];
      return directionSelected && sizeSelected;
    });
  }

  get tableDataPoints(): any[] {
    const selected = this.getSelectedDataPoints();

    // Group by direction and size
    const grouped: Record<string, CubeTrendDataPoint[]> = {};

    selected.forEach(point => {
      const key = `${point.direction.toUpperCase()}_${point.cubeSize}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(point);
    });

    // Find the maximum number of points across all groups
    const maxPoints = Math.max(...Object.values(grouped).map(points => points.length), 0);

    // Create rows
    const rows: any[] = [];
    for (let i = 0; i < maxPoints; i++) {
      const row: any = { index: i + 1 };
      
      this.directions.forEach(direction => {
        if (this.selectedDirections['direction' + direction]) {
          this.cubeSizes.forEach(size => {
            if (this.selectedCubeSizes['size' + size.cubeSizeVoxels]) {
              const key = `${direction.toUpperCase()}_${size.cubeSizeVoxels}`;
              const points = grouped[key] || [];
              const point = points[i];
              
              if (point) {
                const phi = point.totalPorosity != null ? (point.totalPorosity < 1 ? point.totalPorosity * 100 : point.totalPorosity) : null;
                row[`${key}_phi`] = phi != null ? formatValue(String(phi)) : '—';
                row[`${key}_ff`] = formatValue(String(point.formationFactor));
              } else {
                row[`${key}_phi`] = '';
                row[`${key}_ff`] = '';
              }
            }
          });
        }
      });
      
      rows.push(row);
    }

    return rows;
  }

  get displayedCoefficients(): TrendCoefficients[] {
    const selectedDirections = Object.entries(this.selectedDirections)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => key.replace('direction', '').toLowerCase());

    const selectedSizes = Object.entries(this.selectedCubeSizes)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => parseInt(key.replace('size', '')));

    return this.coefficients.filter(coef =>
      selectedDirections.includes(coef.direction.toLowerCase()) &&
      selectedSizes.includes(coef.cubeSize)
    );
  }

  downloadResults(): void {
    if (this.simulation) {
      const selectedData = {
        directions: this.selectedDirections,
        cubeSizes: this.selectedCubeSizes,
      };

      this.simulationsService.downloadResult(this.simulation.id, {
        ...this.selectedDirections,
        ...Object.fromEntries(
          Object.entries(this.selectedCubeSizes).map(([k, v]) => [k, v])
        ),
      });
    }
  }

  hasAnyResults(): boolean {
    return !!this.simulation?.results && this.simulation.results.length > 0;
  }

  isDirectionValid(direction: string): boolean {
    return this.dataPoints.some(
      point => point.direction === direction.toLowerCase()
    );
  }

  isCubeSizeValid(size: number): boolean {
    return this.dataPoints.some(
      point => point.cubeSize === size
    );
  }

  getColorForDirection(direction: string, sizeIndex: number): string {
    const dir = direction.toLowerCase();
    return this.colorScheme[dir]?.[sizeIndex] || '#000000';
  }
}
